<template>
  <section class="atlas-page">
    <div class="atlas-shell">
      <div class="topbar">
        <div class="crumbs">
          <button type="button" class="nav-chip" @click="goPortal">{{ t("portal") }}</button>
          <button type="button" class="nav-chip subtle" @click="goGlossary">{{ t("glossary") }}</button>
        </div>
        <div class="lang-pill">
          <button
            v-for="option in languages"
            :key="option"
            type="button"
            :class="['lang-chip', { active: option === lang }]"
            @click="setLang(option)"
          >
            {{ option.toUpperCase() }}
          </button>
        </div>
      </div>

      <p v-if="loading" class="state-card">{{ t("loading") }}</p>
      <p v-else-if="errorMessage" class="error-panel">{{ errorMessage }}</p>

      <template v-else-if="term">
        <header class="hero-card panel-card">
          <div class="hero-copy">
            <p class="eyebrow">{{ t("eyebrow") }}</p>
            <div class="hero-topline">
              <span class="soft-badge">{{ term.term }}</span>
              <span class="status-chip">{{ term.katakana || term.romaji || term.py || '-' }}</span>
            </div>
            <h1>{{ pickLangText(term.label, lang) }}</h1>
            <p class="lead">{{ pickLangText(term.description, lang) }}</p>
            <div class="chip-row">
              <span v-for="tag in term.tags || []" :key="tag" class="ink-chip">{{ tag }}</span>
            </div>
          </div>

          <aside class="hero-meta">
            <div class="meta-box">
              <span class="meta-label">ID</span>
              <strong>{{ term.termId }}</strong>
            </div>
            <div class="meta-box">
              <span class="meta-label">{{ t("pronunciation") }}</span>
              <strong>{{ term.katakana || '-' }}</strong>
            </div>
            <div class="meta-box">
              <span class="meta-label">Romaji</span>
              <strong>{{ term.romaji || '-' }}</strong>
            </div>
            <div class="meta-box">
              <span class="meta-label">Pinyin</span>
              <strong>{{ term.py || '-' }}</strong>
            </div>
          </aside>
        </header>

        <section v-if="youtubeIds.length || bilibiliIds.length" class="panel-card media-card">
          <div class="section-head">
            <div>
              <p class="section-tag">{{ t("mediaTag") }}</p>
              <h2>{{ t("videos") }}</h2>
            </div>
            <span>{{ t("videoHint") }}</span>
          </div>
          <div class="video-grid">
            <div v-for="videoId in youtubeIds" :key="`yt-${videoId}`" class="video-frame">
              <iframe :src="youtubeEmbedUrl(videoId)" allowfullscreen loading="lazy"></iframe>
            </div>
            <div v-for="videoId in bilibiliIds" :key="`bi-${videoId}`" class="video-frame">
              <iframe :src="bilibiliEmbedUrl(videoId)" allowfullscreen loading="lazy"></iframe>
            </div>
          </div>
        </section>

        <section class="split-stage">
          <article class="panel-card technique-card-wrap">
            <div class="section-head">
              <div>
                <p class="section-tag">{{ t("techniquesTag") }}</p>
                <h2>{{ t("relatedTechniques") }}</h2>
              </div>
              <span>{{ term.relatedTechniques?.length || 0 }} {{ t("items") }}</span>
            </div>

            <div v-if="!term.relatedTechniques?.length" class="empty-state">
              {{ t("emptyTechniques") }}
            </div>

            <div v-else class="card-list">
              <article
                v-for="item in term.relatedTechniques"
                :key="item.techniqueTermId"
                class="section-card clickable-card"
                @click="openTechnique(item.technique.slug)"
              >
                <div class="hero-topline">
                  <span class="soft-badge">{{ getCategoryLabel(item.technique.category, lang) }}</span>
                  <span class="status-chip">{{ getRelationLabel(item.relationType, lang) }}</span>
                </div>
                <h3>{{ pickLangText(item.technique.item, lang) }}</h3>
                <p>{{ t("effectiveDate") }}: {{ formatDateOnly(item.technique.effectiveDate) }}</p>
                <div class="chip-row">
                  <span v-for="tag in item.technique.tags || []" :key="tag" class="ink-chip">{{ tag }}</span>
                </div>
              </article>
            </div>
          </article>

          <article class="panel-card topic-card-wrap">
            <div class="section-head">
              <div>
                <p class="section-tag">{{ t("topicsTag") }}</p>
                <h2>{{ t("relatedTopics") }}</h2>
              </div>
              <span>{{ term.relatedTopics?.length || 0 }} {{ t("items") }}</span>
            </div>

            <div v-if="!term.relatedTopics?.length" class="empty-state">
              {{ t("emptyTopics") }}
            </div>

            <div v-else class="card-list">
              <article
                v-for="item in term.relatedTopics"
                :key="item.topicTermId"
                class="section-card clickable-card"
                @click="openTopic(item.topic.slug)"
              >
                <div class="hero-topline">
                  <span class="soft-badge">
                    {{ item.topic.episodeNo ? `EP ${item.topic.episodeNo}` : getCategoryLabel(item.topic.topicType, lang) }}
                  </span>
                  <span class="status-chip">{{ getRelationLabel(item.relationType, lang) }}</span>
                </div>
                <h3>{{ pickLangText(item.topic.title, lang) }}</h3>
                <p>{{ summarizeText(item.topic.summary, lang, 150) }}</p>
                <p class="term-pron">
                  {{ item.topic.seriesTitle ? pickLangText(item.topic.seriesTitle, lang) : t("independent") }}
                </p>
              </article>
            </div>
          </article>
        </section>
      </template>
    </div>
  </section>
</template>

<script setup>
import axios from "axios";
import { computed, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import {
  API_BASE,
  ensureApiObject,
  bilibiliEmbedUrl,
  formatDateOnly,
  getCategoryLabel,
  getRelationLabel,
  pickLangText,
  summarizeText,
  toArray,
  youtubeEmbedUrl,
} from "./portalShared";
import { useSiteLanguage } from "../../store/siteLanguage";

const route = useRoute();
const router = useRouter();
const { lang, languages, setLang } = useSiteLanguage();

const copy = {
  ja: {
    portal: "ポータル",
    glossary: "術語一覧",
    loading: "術語詳細を読込中...",
    eyebrow: "GLOSSARY RADAR",
    pronunciation: "かな",
    mediaTag: "VIDEO REVIEW",
    videos: "Videos",
    videoHint: "この術語に紐づく動画。",
    techniquesTag: "PRACTICE LINKS",
    relatedTechniques: "Related Techniques",
    topicsTag: "TOPIC LINKS",
    relatedTopics: "Related Topics",
    items: "件",
    emptyTechniques: "この術語に関連する技術はまだありません。",
    emptyTopics: "この術語に関連するトピックはまだありません。",
    effectiveDate: "反映日",
    independent: "独立テーマ",
  },
  zh: {
    portal: "门户",
    glossary: "术语列表",
    loading: "正在读取术语详情...",
    eyebrow: "GLOSSARY RADAR",
    pronunciation: "假名",
    mediaTag: "VIDEO REVIEW",
    videos: "Videos",
    videoHint: "与这个术语相关的视频。",
    techniquesTag: "PRACTICE LINKS",
    relatedTechniques: "Related Techniques",
    topicsTag: "TOPIC LINKS",
    relatedTopics: "Related Topics",
    items: "条",
    emptyTechniques: "这个术语还没有关联技术。",
    emptyTopics: "这个术语还没有关联话题。",
    effectiveDate: "生效日",
    independent: "独立主题",
  },
  en: {
    portal: "Portal",
    glossary: "Glossary",
    loading: "Loading term detail...",
    eyebrow: "GLOSSARY RADAR",
    pronunciation: "Kana",
    mediaTag: "VIDEO REVIEW",
    videos: "Videos",
    videoHint: "Media linked to this term.",
    techniquesTag: "PRACTICE LINKS",
    relatedTechniques: "Related Techniques",
    topicsTag: "TOPIC LINKS",
    relatedTopics: "Related Topics",
    items: "items",
    emptyTechniques: "No related techniques are linked to this term yet.",
    emptyTopics: "No related topics are linked to this term yet.",
    effectiveDate: "Effective date",
    independent: "Independent topic",
  },
};

function t(key) {
  return copy[lang.value]?.[key] || key;
}

const term = ref(null);
const loading = ref(true);
const errorMessage = ref("");
const youtubeIds = computed(() => toArray(term.value?.youtubeVideoId));
const bilibiliIds = computed(() => toArray(term.value?.bilibiliVideoId));

async function loadTerm() {
  loading.value = true;
  errorMessage.value = "";

  try {
    const response = await axios.get(`${API_BASE}/api/kendou/terms/${encodeURIComponent(route.params.termId)}`, {
      timeout: 10000,
    });
    term.value = ensureApiObject(response, "Kendou term API");
  } catch (error) {
    console.error(error);
    term.value = null;
    errorMessage.value = error?.response?.status === 404 ? "Term not found." : error?.message || "Failed to load term.";
  } finally {
    loading.value = false;
  }
}

function goPortal() {
  router.push("/KendouPage/terminology");
}

function goGlossary() {
  router.push("/KendouPage/terminology?section=terms");
}

function openTechnique(slug) {
  router.push(`/KendouPage/technique/${encodeURIComponent(slug)}`);
}

function openTopic(slug) {
  router.push(`/KendouPage/topic/${encodeURIComponent(slug)}`);
}

watch(() => route.params.termId, loadTerm);
onMounted(loadTerm);
</script>

<style scoped>
.atlas-page {
  --paper: rgba(251, 245, 235, 0.92);
  --paper-strong: rgba(255, 250, 242, 0.98);
  --ink: #2f2116;
  --ink-soft: #5d4b3d;
  --line: rgba(182, 148, 110, 0.34);
  min-height: 100vh;
  padding: 20px;
  background:
    radial-gradient(circle at 16% 12%, rgba(255, 255, 255, 0.7), transparent 28%),
    radial-gradient(circle at 86% 82%, rgba(235, 190, 116, 0.28), transparent 26%),
    linear-gradient(135deg, #e7d8bf 0%, #efe6d3 24%, #f5eedf 48%, #eadab8 100%);
}

.atlas-shell { max-width: 1380px; margin: 0 auto; display: grid; gap: 18px; }
.topbar, .crumbs, .hero-topline, .chip-row, .section-head { display: flex; gap: 10px; flex-wrap: wrap; justify-content: space-between; }
.lang-pill { display: inline-flex; gap: 8px; padding: 8px; border: 1px solid rgba(191, 169, 139, 0.72); border-radius: 999px; background: rgba(255, 250, 245, 0.8); box-shadow: 0 10px 24px rgba(106, 75, 41, 0.08); }
.lang-chip, .nav-chip { border-radius: 999px; cursor: pointer; }
.lang-chip { border: 1px solid transparent; background: transparent; color: #604d3d; font-family: "Cormorant Garamond", Georgia, serif; font-size: 1rem; padding: 10px 16px; transition: transform 0.22s ease, box-shadow 0.22s ease, background-color 0.22s ease, color 0.22s ease, border-color 0.22s ease; }
.lang-chip.active { background: linear-gradient(135deg, #b64926 0%, #8a2613 100%); color: #fff8f3; }
.nav-chip { border: 1px solid rgba(177, 144, 108, 0.7); background: rgba(255, 249, 240, 0.88); color: #5b4636; padding: 10px 15px; }
.nav-chip.subtle { background: rgba(245, 236, 223, 0.95); }
.panel-card, .state-card, .error-panel, .section-card, .media-card { border: 1px solid var(--line); border-radius: 28px; background: linear-gradient(180deg, var(--paper-strong) 0%, var(--paper) 100%); box-shadow: 0 22px 44px rgba(118, 87, 49, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.75); }
.hero-card, .media-card, .technique-card-wrap, .topic-card-wrap, .state-card, .error-panel { padding: 20px 22px 22px; }
.hero-card { display: grid; grid-template-columns: 1.35fr 0.85fr; gap: 18px; }
.eyebrow, .section-tag { margin: 0 0 10px; font-family: "Cormorant Garamond", "Times New Roman", serif; font-size: 14px; letter-spacing: 0.2em; text-transform: uppercase; color: #9c341d; }
.hero-card h1, .section-head h2, .section-card h3 { margin: 0; color: var(--ink); }
.hero-card h1 { font-family: "Noto Serif JP", "Yu Mincho", Georgia, serif; font-size: clamp(2.4rem, 4.2vw, 4rem); line-height: 1.1; }
.lead, .hero-meta, .section-card p, .section-head span, .state-card, .error-panel { color: var(--ink-soft); line-height: 1.8; }
.hero-meta { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; align-content: start; }
.meta-box { border: 1px solid rgba(216, 180, 139, 0.58); border-radius: 20px; background: rgba(255, 248, 239, 0.82); padding: 16px; display: grid; gap: 6px; }
.meta-label { font-size: 0.8rem; letter-spacing: 0.08em; text-transform: uppercase; color: #7a5d44; }
.meta-box strong { color: var(--ink); font-family: "Cormorant Garamond", Georgia, serif; font-size: 1.2rem; word-break: break-word; }
.soft-badge, .status-chip, .ink-chip { display: inline-flex; align-items: center; border-radius: 999px; padding: 6px 11px; font-size: 0.78rem; letter-spacing: 0.06em; }
.soft-badge { background: rgba(235, 220, 201, 0.9); color: #6d5138; }
.status-chip { background: linear-gradient(135deg, #442616 0%, #24130b 100%); color: #fff7f0; }
.ink-chip { background: rgba(245, 233, 216, 0.95); color: #705940; }
.video-grid, .card-list, .split-stage { display: grid; gap: 14px; }
.video-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
.split-stage { grid-template-columns: 1fr 1fr; }
.video-frame { position: relative; padding-bottom: 56.25%; overflow: hidden; border-radius: 20px; border: 1px solid rgba(205, 175, 140, 0.75); background: #1a120a; }
.video-frame iframe { position: absolute; inset: 0; width: 100%; height: 100%; border: 0; }
.section-card { padding: 16px 18px 18px; }
.clickable-card { cursor: pointer; transition: transform 0.22s ease, border-color 0.22s ease, box-shadow 0.22s ease; }
.clickable-card:hover { transform: translateY(-4px); border-color: rgba(184, 94, 55, 0.65); box-shadow: 0 18px 30px rgba(117, 78, 40, 0.16), inset 0 1px 0 rgba(255, 255, 255, 0.75); }
.term-pron { color: #7c654c; }
.empty-state { color: var(--ink-soft); line-height: 1.8; }
@media (max-width: 1080px) { .hero-card, .split-stage, .video-grid { grid-template-columns: 1fr; } }
@media (max-width: 760px) { .atlas-page { padding: 14px; } .topbar { flex-direction: column; align-items: flex-start; } .lang-pill { align-self: flex-start; } .hero-meta { grid-template-columns: 1fr; } }
</style>
